import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MessageCircle, Shield, Zap, Users, ArrowRight, Play, Search, MapPin, Coins } from 'lucide-react'
import { ModelsGrid } from '../components/ModelsGrid'
import { useAuth } from '../hooks/useAuth'
import { useApproxLocation } from '../hooks/useApproxLocation'
import { tokenService } from '../services/tokenService'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { TokenPackage } from '../types'

export function HomePageNew() {
  const { user } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterOnline, setFilterOnline] = useState(false)
  const [sortBy, setSortBy] = useState('newest')
  const [useDeviceLocation, setUseDeviceLocation] = useState(false)
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [highlightedOffer, setHighlightedOffer] = useState<TokenPackage | null>(null)
  const [showTokenPromoBubble, setShowTokenPromoBubble] = useState(true)

  const tokenPromoStorageKey = 'nlbr_token_promo_hidden_until'

  const approx = useApproxLocation()

  const fallbackCity = user?.user_metadata?.city as string | undefined
  const fallbackState = user?.user_metadata?.state as string | undefined

  const nearbyCity = useDeviceLocation ? (approx.city || fallbackCity) : fallbackCity
  const nearbyState = useDeviceLocation ? (approx.state || fallbackState) : fallbackState
  const hasNearby = !!nearbyState || !!nearbyCity

  const features = [
    {
      icon: MessageCircle,
      title: 'Chat Público em Tempo Real',
      description: 'Converse com suas modelos favoritas em salas de chat públicas estilo UOL',
      color: 'text-blue-400'
    },
    {
      icon: Zap,
      title: 'Sistema de Tokens',
      description: 'Desbloqueie fotos exclusivas usando tokens. Sistema justo para todos',
      color: 'text-yellow-400'
    },
    {
      icon: Shield,
      title: '100% Seguro',
      description: 'Proteção anti-print screen e sistema de pagamento seguro via PIX',
      color: 'text-green-400'
    },
    {
      icon: Users,
      title: 'Comunidade Ativa',
      description: 'Milhares de usuários online interagindo em tempo real',
      color: 'text-purple-400'
    }
  ]

  const [stats, setStats] = useState([
    { number: '0', label: 'Modelos Ativas' },
    { number: '0', label: 'Usuários Ativos' },
    { number: '0', label: 'Fotos Compradas' },
    { number: '99.9%', label: 'Uptime' }
  ])

  useEffect(() => {
    // Persistir "fechar" da bolha no mobile (evita voltar toda vez)
    try {
      const raw = localStorage.getItem(tokenPromoStorageKey)
      const hiddenUntil = raw ? Number(raw) : 0
      if (hiddenUntil && Number.isFinite(hiddenUntil) && hiddenUntil > Date.now()) {
        setShowTokenPromoBubble(false)
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    let mounted = true
    const loadOffer = async () => {
      const data = await tokenService.fetchTokenPackages()
      if (!mounted) return

      const active = data.filter(pkg => pkg.is_active !== false)
      const featured = active.find(pkg => pkg.is_featured)
      setHighlightedOffer(featured || active[0] || null)
    }

    loadOffer()
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    let mounted = true

    const fetchStats = async () => {
      if (!isSupabaseConfigured) {
        if (mounted) {
          setStats([
            { number: '0', label: 'Modelos Ativas' },
            { number: '0', label: 'Usuários Ativos' },
            { number: '0', label: 'Fotos Compradas' },
            { number: '99.9%', label: 'Uptime' }
          ])
        }
        return
      }

      try {
        // Modelos ativas: tenta filtrar por schema atual; se falhar, usa total de perfis.
        let modelsCount = 0
        try {
          const { count, error } = await supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
            .eq('is_model', true)
            .eq('approval_status', 'approved')
            .neq('is_blocked', true)

          if (error) throw error
          modelsCount = count || 0
        } catch {
          const { count } = await supabase
            .from('profiles')
            .select('id', { count: 'exact', head: true })
          modelsCount = count || 0
        }

        // Usuários ativos: total de clientes
        let usersCount = 0
        try {
          const { count } = await supabase
            .from('clients')
            .select('id', { count: 'exact', head: true })
          usersCount = count || 0
        } catch {
          usersCount = 0
        }

        // Fotos compradas: total de desbloqueios de imagens
        let photosCount = 0
        try {
          const { count } = await supabase
            .from('image_unlocks')
            .select('id', { count: 'exact', head: true })
          photosCount = count || 0
        } catch {
          photosCount = 0
        }

        if (mounted) {
          setStats([
            { number: String(modelsCount), label: 'Modelos Ativas' },
            { number: String(usersCount), label: 'Usuários Ativos' },
            { number: String(photosCount), label: 'Fotos Compradas' },
            { number: '99.9%', label: 'Uptime' }
          ])
        }
      } catch {
        if (mounted) {
          setStats([
            { number: '0', label: 'Modelos Ativas' },
            { number: '0', label: 'Usuários Ativos' },
            { number: '0', label: 'Fotos Compradas' },
            { number: '99.9%', label: 'Uptime' }
          ])
        }
      }
    }

    fetchStats()

    return () => {
      mounted = false
    }
  }, [])

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Mobile: token promo bubble (keeps offer visible without ocupar espaço) */}
      {highlightedOffer && showTokenPromoBubble && (
        <motion.div
          className="fixed bottom-4 right-4 z-40 md:hidden"
          animate={{ x: [0, -2, 2, -2, 2, 0] }}
          transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2.2 }}
        >
          <Link
            to="/tokens"
            className="group relative flex items-center gap-3 rounded-full border border-pink-500/30 bg-gray-900/90 px-4 py-3 shadow-lg backdrop-blur hover:border-pink-500/50"
            aria-label="Promoção de tokens"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-600/20 text-pink-200">
              <Coins className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-pink-200/90 leading-4">
                Promoção de tokens
              </p>
              <p className="text-sm font-semibold text-white leading-5">
                {highlightedOffer.tokens}
                {highlightedOffer.bonus ? (
                  <span className="text-green-300">+{highlightedOffer.bonus}</span>
                ) : null}
                <span className="text-white/70 font-normal ml-1">por R$ {highlightedOffer.price.toFixed(2)}</span>
              </p>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                try {
                  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
                  localStorage.setItem(tokenPromoStorageKey, String(Date.now() + sevenDaysMs))
                } catch {
                  // ignore
                }
                setShowTokenPromoBubble(false)
              }}
              className="absolute -top-2 -left-2 h-7 w-7 rounded-full border border-gray-700 bg-gray-900 text-gray-300 shadow hover:text-white"
              aria-label="Fechar promoção"
            >
              ×
            </button>
          </Link>
        </motion.div>
      )}

      {/* Compact Hero Section */}
      <section className="relative overflow-hidden py-6 md:py-10">
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-br from-pink-900/20 via-purple-900/20 to-blue-900/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-4xl font-bold text-white mb-3"
            >
              A Nova Era do
              <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                {' '}Entretenimento Premium
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-gray-300 mb-5 md:mb-6 max-w-2xl mx-auto"
            >
              Chat público em tempo real, fotos exclusivas e a melhor experiência com modelos do Brasil.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3 justify-center items-center"
            >
              {!user ? (
                <>
                  <Link
                    to="/register"
                    className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                  >
                    <span>Começar Agora</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/models"
                    className="border border-gray-600 hover:border-gray-500 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                  >
                    <Play className="w-4 h-4" />
                    <span>Ver Modelos</span>
                  </Link>
                </>
              ) : (
                <Link
                  to="/models"
                  className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Entrar nos Chats</span>
                </Link>
              )}
            </motion.div>
          </div>

          {highlightedOffer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0, scale: [1, 1.02, 1] }}
              transition={{ delay: 0.25, duration: 2.4, repeat: Infinity, repeatDelay: 1.6 }}
              className="mt-8 hidden md:block"
            >
              <Link
                to="/tokens"
                className="mx-auto flex max-w-3xl items-center justify-between gap-4 rounded-2xl border border-pink-500/30 bg-gradient-to-r from-pink-600/20 via-purple-600/20 to-blue-600/20 px-5 py-4 text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-500/20 text-pink-200">
                    <Coins className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-pink-200">Oferta de tokens</p>
                    <p className="text-base font-semibold text-white">
                      {highlightedOffer.description || 'Pacote em destaque'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <div className="text-right">
                    <p className="text-lg font-semibold">
                      {highlightedOffer.tokens}
                      {highlightedOffer.bonus ? (
                        <span className="text-green-300">+{highlightedOffer.bonus}</span>
                      ) : null}
                      <span className="text-sm text-white/70 ml-2">tokens</span>
                    </p>
                    <p className="text-sm text-pink-200">R$ {highlightedOffer.price.toFixed(2)}</p>
                  </div>
                  <span className="rounded-lg bg-pink-600 px-4 py-2 text-sm font-semibold text-white">
                    Comprar
                  </span>
                </div>
              </Link>
            </motion.div>
          )}
        </div>
      </section>

      {/* Models Preview - NOW FIRST */}
      <section className="py-6 md:py-10 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: quick search + filters toggle (show cards ASAP) */}
          <div className="lg:hidden mb-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar modelos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>
              <button
                onClick={() => setShowMobileFilters(v => !v)}
                className="px-4 py-2 rounded-lg border border-gray-700 text-white hover:border-gray-600 transition-colors"
              >
                Filtros
              </button>
            </div>

            {showMobileFilters && (
              <div className="mt-3 bg-gray-800 rounded-lg p-4 space-y-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterOnline}
                    onChange={(e) => setFilterOnline(e.target.checked)}
                    className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500"
                  />
                  <span className="text-white">Apenas online</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useDeviceLocation}
                    onChange={(e) => {
                      const next = e.target.checked
                      setUseDeviceLocation(next)

                      if (next) {
                        approx.request()
                        setSortBy('nearby')
                      } else {
                        approx.reset()
                        if (sortBy === 'nearby') setSortBy('newest')
                      }
                    }}
                    className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500"
                  />
                  <span className="text-white flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-pink-400" />
                    Próximas de mim
                  </span>
                </label>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  <option value="newest">Mais recentes</option>
                  <option value="popular">Mais populares</option>
                  <option value="online">Online primeiro</option>
                  <option value="nearby">Perto de você</option>
                  <option value="rating">Melhor avaliadas</option>
                  <option value="liked">Somente curtidas</option>
                  <option value="followed">Somente seguidas</option>
                </select>

                {useDeviceLocation && approx.status === 'requesting_gps' && (
                  <div className="text-sm text-gray-300/90">
                    Ative a permissão de localização no navegador…
                  </div>
                )}

                {useDeviceLocation && approx.status === 'reverse_geocoding' && (
                  <div className="text-sm text-gray-300/90">
                    Detectando sua cidade/estado…
                  </div>
                )}

                {useDeviceLocation && approx.status === 'error' && (
                  <div className="text-sm text-yellow-300/90">
                    {approx.error || 'Não foi possível obter sua localização.'}
                  </div>
                )}

                {useDeviceLocation && !hasNearby && approx.status === 'ready' && (
                  <div className="text-sm text-yellow-300/90">
                    Não foi possível detectar cidade/estado pela localização. Preencha cidade/estado no cadastro para usar aproximação.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Desktop: full filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="hidden lg:block bg-gray-800 rounded-lg p-6 mb-6"
          >
            <div className="flex flex-col lg:flex-row gap-4 items-center">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar modelos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500"
                />
              </div>

              {/* Online Filter */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterOnline}
                  onChange={(e) => setFilterOnline(e.target.checked)}
                  className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500"
                />
                <span className="text-white">Apenas online</span>
              </label>

              {/* Approx Location */}
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useDeviceLocation}
                  onChange={(e) => {
                    const next = e.target.checked
                    setUseDeviceLocation(next)

                    if (next) {
                      approx.request()
                      setSortBy('nearby')
                    } else {
                      approx.reset()
                      if (sortBy === 'nearby') setSortBy('newest')
                    }
                  }}
                  className="w-4 h-4 text-pink-600 bg-gray-700 border-gray-600 rounded focus:ring-pink-500"
                />
                <span className="text-white flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-pink-400" />
                  Próximas de mim (localização)
                </span>
              </label>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="newest">Mais recentes</option>
                <option value="popular">Mais populares</option>
                <option value="online">Online primeiro</option>
                <option value="nearby">Perto de você</option>
                <option value="rating">Melhor avaliadas</option>
                <option value="liked">Somente curtidas</option>
                <option value="followed">Somente seguidas</option>
              </select>
            </div>

            {useDeviceLocation && approx.status === 'requesting_gps' && (
              <div className="mt-3 text-sm text-gray-300/90">
                Ative a permissão de localização no navegador€¦
              </div>
            )}

            {useDeviceLocation && approx.status === 'reverse_geocoding' && (
              <div className="mt-3 text-sm text-gray-300/90">
                Detectando sua cidade/estado€¦
              </div>
            )}

            {useDeviceLocation && approx.status === 'error' && (
              <div className="mt-3 text-sm text-yellow-300/90">
                {approx.error || 'Não foi possível obter sua localização.'}
              </div>
            )}

            {useDeviceLocation && !hasNearby && approx.status === 'ready' && (
              <div className="mt-3 text-sm text-yellow-300/90">
                Não foi possível detectar cidade/estado pela localização. Preencha cidade/estado no cadastro para usar aproximação.
              </div>
            )}
          </motion.div>

          <ModelsGrid
            limit={8}
            showTitle={false}
            searchTerm={searchTerm}
            filterOnline={filterOnline}
            sortBy={sortBy}
            nearbyCity={nearbyCity}
            nearbyState={nearbyState}
            showTokenOffer={false}
          />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-10 md:py-16 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2 md:mb-3">
              Por que escolher a Next Level BR?
            </h2>
            <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto">
              A plataforma mais inovadora do Brasil com recursos únicos
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center bg-gray-900/40 border border-gray-800 rounded-xl p-4 md:p-6"
              >
                <div className={`inline-flex items-center justify-center w-10 h-10 md:w-14 md:h-14 rounded-full bg-gray-800 mb-2 md:mb-3 ${feature.color}`}>
                  <feature.icon className="w-5 h-5 md:w-7 md:h-7" />
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-white">
                  {feature.title}
                </h3>
                <p className="hidden md:block text-sm text-gray-400 mt-2">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gradient-to-r from-pink-900/20 to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pink-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Pronto para começar?
          </h2>
          <p className="text-lg text-pink-100 mb-6">
            Junte-se a milhares de usuários que já descobriram a melhor plataforma de entretenimento premium do Brasil
          </p>
          
          {!user ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/register?type=client"
                className="bg-white text-pink-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Cadastrar como Cliente
              </Link>
              <Link
                to="/register?type=model"
                className="border-2 border-white text-white px-6 py-2 rounded-lg font-semibold hover:bg-white hover:text-pink-600 transition-colors"
              >
                Cadastrar como Modelo
              </Link>
            </div>
          ) : (
            <Link
              to="/models"
              className="bg-white text-pink-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center space-x-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Explorar Agora</span>
            </Link>
          )}
        </div>
      </section>
    </div>
  )
}



